import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v3Api } from '../hooks/useApi';

function formatDate(d) {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return String(d).slice(0, 10); }
}

function nextUpcoming(occurrences) {
    if (!Array.isArray(occurrences) || !occurrences.length) return null;
    const now = Date.now();
    const upcoming = occurrences
        .filter(o => o.start_date && new Date(o.start_date).getTime() >= now)
        .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    return upcoming[0] || null;
}

/**
 * Table of seasonal events with edit/delete + accordion of occurrences.
 *
 * Props: { events, loading, error, onEdit, onCreate, onChanged }
 */
export default function EventList({ events, loading, error, onEdit, onCreate, onChanged }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(() => new Set());
    const [busyKey, setBusyKey] = useState(null);

    const toggle = (key) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const handleDelete = useCallback(async (ev) => {
        if (!window.confirm(t('Delete event "{{name}}"? This cannot be undone.', { name: ev.event_name }))) return;
        setBusyKey(ev.event_key);
        try {
            await v3Api.seasonal.remove(ev.event_key);
            onChanged?.();
        } catch (err) {
            alert(err?.response?.data?.error || err?.message || t('Delete failed'));
        } finally {
            setBusyKey(null);
        }
    }, [onChanged, t]);

    if (loading) {
        return <div className="v3-card" style={{ padding: 20 }}>{t('Loading events…')}</div>;
    }
    if (error) {
        return (
            <div className="v3-card" style={{ padding: 20, color: 'var(--v3-text-danger, #ef4444)' }}>
                {t('Error')}: {error}
            </div>
        );
    }
    if (!events?.length) {
        return (
            <div className="v3-card v3-seasonal-empty">
                <div className="v3-body" style={{ marginBottom: 12 }}>
                    {t('No events yet — create one or run the seed migration.')}
                </div>
                <button onClick={onCreate} className="v3-btn v3-btn-primary">{t('+ Create event')}</button>
            </div>
        );
    }

    return (
        <div className="v3-card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="v3-seasonal-table">
                <thead>
                    <tr>
                        <th>{t('Event')}</th>
                        <th>{t('Key')}</th>
                        <th>{t('Category')}</th>
                        <th style={{ textAlign: 'right' }}>{t('# Occurrences')}</th>
                        <th>{t('Next upcoming')}</th>
                        <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {events.map(ev => {
                        const isOpen = expanded.has(ev.event_key);
                        const next = nextUpcoming(ev.occurrences);
                        const cat = (ev.category || 'other').toLowerCase();
                        return (
                            <React.Fragment key={ev.event_key}>
                                <tr className="v3-seasonal-row" onClick={() => toggle(ev.event_key)}>
                                    <td>
                                        <span style={{ marginRight: 6, color: 'var(--v3-text-muted)' }}>
                                            {isOpen ? '▾' : '▸'}
                                        </span>
                                        {ev.event_name}
                                    </td>
                                    <td><code style={{ fontSize: 11 }}>{ev.event_key}</code></td>
                                    <td>
                                        <span className={`v3-seasonal-chip v3-chip-${cat}`}>{cat}</span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{ev.occurrences?.length || 0}</td>
                                    <td>{next ? `${formatDate(next.start_date)} (${next.year})` : '—'}</td>
                                    <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                        <button
                                            className="v3-seasonal-icon-btn"
                                            onClick={() => onEdit?.(ev)}
                                            style={{ marginRight: 6 }}
                                        >{t('Edit')}</button>
                                        <button
                                            className="v3-seasonal-icon-btn danger"
                                            disabled={busyKey === ev.event_key}
                                            onClick={() => handleDelete(ev)}
                                        >{busyKey === ev.event_key ? '…' : t('Delete')}</button>
                                    </td>
                                </tr>
                                {isOpen && (
                                    <tr>
                                        <td colSpan={6} style={{ padding: 0 }}>
                                            <div className="v3-seasonal-occurrences">
                                                {ev.description && (
                                                    <div style={{ marginBottom: 6, color: 'var(--v3-text-muted)' }}>
                                                        {ev.description}
                                                    </div>
                                                )}
                                                {Array.isArray(ev.occurrences) && ev.occurrences.length ? (
                                                    <ul>
                                                        {ev.occurrences
                                                            .slice()
                                                            .sort((a, b) => (a.year || 0) - (b.year || 0))
                                                            .map((o, i) => (
                                                                <li key={`${ev.event_key}-${o.year}-${i}`}>
                                                                    <strong>{o.year}</strong>: {formatDate(o.start_date)} → {formatDate(o.end_date)}
                                                                </li>
                                                            ))}
                                                    </ul>
                                                ) : (
                                                    <span>{t('No occurrences recorded.')}</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
